import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { DocumentGenericField } from '@domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, switchMap } from 'rxjs';
import { GenericImageFieldPartial } from 'src/app/domain/entities/partials/generics/generic-image-field.partial';
import { GenericTextFieldPartial } from 'src/app/domain/entities/partials/generics/generic-text-field.partial';
import { GenericItemTypeEnum } from 'src/app/domain/enums/generic-item-type.enum';
import { RaToastService } from '@shared/components';
import { RaSwitchComponent } from 'ra-ui';
import { RaButtonComponent } from 'src/app/shared/components/ra-button/ra-button.component';
import {
    DATA_TAG_CONTEXT,
    DataTagContext,
} from 'src/app/shared/components/ra-editor/data-tag-context.token';
import { getDataTagLabel } from 'src/app/shared/components/ra-editor/data-tag-i18n';
import { resolveDataTagValue } from 'src/app/shared/components/ra-editor/data-tag-value.resolver';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import {
    LinkTokenComposerValue,
    RaLinkTokenComposerComponent,
} from 'src/app/shared/components/ra-link-token-composer/ra-link-token-composer.component';
import { sanitizeGenericFields } from 'src/app/shared/utils/generic-fields.util';
import { MediaUtils } from 'src/app/shared/utils/media.utils';
import { EditService } from '../../../services/edit-service';

export interface HeaderEditorSaveEvent {
    header: DocumentGenericField[];
}

/** Ícone por `fieldId` (limitado aos ícones disponíveis na web). */
const FIELD_ICONS: Record<string, string> = {
    logo: 'gallery',
    custom_description: 'text',
    company_document: 'document-copy',
    company_email: 'email',
};

const COMPANY_LOGO_TOKEN = '$COMPANY_LOGO';
const COMPANY_LOGO_TAG = 'link_company_logo';

@Component({
    selector: 'modal-header-editor',
    templateUrl: 'modal-header-editor.component.html',
    styleUrl: 'modal-header-editor.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslatePipe,
        RaModalComponent,
        RaButtonComponent,
        RaSwitchComponent,
        RaIconComponent,
        RaLinkTokenComposerComponent,
    ],
})
export class ModalHeaderEditorComponent {
    private _translate = inject(TranslateService);
    private _toast = inject(RaToastService);
    private _media = inject(MediaUtils);
    private _editService = inject(EditService);
    private _dataTagContext = inject(DATA_TAG_CONTEXT, { optional: true });

    public header = input.required<DocumentGenericField[]>();
    public loading = input<boolean>(false);
    /**
     * Override do upload do logo (níveis order/company). Quando ausente, usa o
     * upload document-scoped do `EditService`. Permite reusar o editor fora do documento.
     */
    public logoUploaderFn = input<((file: File) => Observable<string>) | null>(null);

    public close = output<void>();
    public save = output<HeaderEditorSaveEvent>();

    public readonly MAX_VISIBLE = 3;
    public readonly TEXT = GenericItemTypeEnum.TEXT;
    public readonly IMAGE = GenericItemTypeEnum.IMAGE;

    /** Cópia de trabalho local, semeada uma vez na abertura. */
    public items = signal<DocumentGenericField[]>([]);
    public visibilityControls = new Map<string, FormControl<boolean>>();
    public logoUploading = signal(false);

    private _seeded = false;

    /** Itens editáveis (logo IMAGE + campos TEXT). */
    public editableItems = computed(() =>
        this.items().filter(
            i => i.canEdit && (i.type === GenericItemTypeEnum.TEXT || i.type === GenericItemTypeEnum.IMAGE)
        )
    );

    /** Conta os itens editáveis visíveis (inclui o logo, sempre visível) — bate com o badge. */
    public visibleCount = computed(() => this.editableItems().filter(i => i.isVisible).length);

    public isAtMax = computed(() => this.visibleCount() >= this.MAX_VISIBLE);

    public canAct = computed(() => this.visibleCount() > 0 && !this.loading());

    /** URL da logo da empresa (resolve o token $COMPANY_LOGO no preview/thumb). */
    public companyLogo = computed(() => this._dataTagContext?.()?.company?.logo ?? '');

    /** Logo a exibir no preview (se o item logo estiver visível). */
    public previewLogo = computed(() => {
        const logo = this.items().find(i => i.type === GenericItemTypeEnum.IMAGE && i.isVisible);
        return logo ? this.logoUrl(logo) : '';
    });

    /** Textos resolvidos para o preview. */
    public previewTexts = computed(() => {
        const ctx = this._dataTagContext?.() ?? null;
        return this.items()
            .filter(i => i.isVisible && i.type === GenericItemTypeEnum.TEXT)
            .map(i => this._resolveDisplay((i.value ?? {}) as GenericTextFieldPartial, ctx))
            .filter(text => (text ?? '').trim().length > 0);
    });

    constructor() {
        effect(() => {
            const header = this.header();
            if (this._seeded) return;
            this._seeded = true;
            untracked(() => this._seed(header));
        });

        // No limite, desabilita os toggles desligados (logo não tem toggle: canHide=false).
        effect(() => {
            const atMax = this.isAtMax();
            const list = this.items();
            untracked(() => {
                for (const it of list) {
                    const c = it.fieldId ? this.visibilityControls.get(it.fieldId) : undefined;
                    if (!c) continue;
                    const shouldDisable = atMax && !it.isVisible;
                    if (shouldDisable && !c.disabled) c.disable({ emitEvent: false });
                    else if (!shouldDisable && c.disabled) c.enable({ emitEvent: false });
                }
            });
        });
    }

    private _seed(header: DocumentGenericField[]): void {
        // O spread abaixo transforma um item fora de forma (ex.: `[]` vindo do banco)
        // num `{}`, que seria devolvido no save. Filtra antes. Ver [sanitizeGenericFields].
        const clone: DocumentGenericField[] = sanitizeGenericFields(header).map(item => ({
            ...item,
            value: item.value ? { ...item.value } : item.value,
        }));
        this.items.set(clone);

        this.visibilityControls.clear();
        for (const item of clone) {
            // Só campos ocultáveis (canHide) têm toggle; o logo é sempre visível.
            if (!item.fieldId || !item.canHide) continue;
            const fieldId = item.fieldId;
            const ctrl = new FormControl<boolean>(!!item.isVisible, { nonNullable: true });
            ctrl.valueChanges.subscribe(val => this._onToggle(fieldId, val));
            this.visibilityControls.set(fieldId, ctrl);
        }
    }

    public controlFor(fieldId?: string): FormControl<boolean> | null {
        return (fieldId && this.visibilityControls.get(fieldId)) || null;
    }

    public onCardHeadClick(item: DocumentGenericField): void {
        if (this.isAtMax() && !item.isVisible && item.canHide) {
            this._toast.warning({
                message: this._translate.instant('document.edit.headerEditor.maxItems', {
                    max: this.MAX_VISIBLE,
                }),
            });
        }
    }

    private _onToggle(fieldId: string, val: boolean): void {
        this.items.update(list =>
            list.map(i => (i.fieldId === fieldId ? { ...i, isVisible: val } : i))
        );
    }

    public onComposerChange(fieldId: string | undefined, value: LinkTokenComposerValue): void {
        if (!fieldId) return;
        this.items.update(list =>
            list.map(item => {
                if (item.fieldId !== fieldId) return item;
                const v: GenericTextFieldPartial = { ...(item.value as GenericTextFieldPartial) };
                if (value.link) {
                    v.text = value.link.value || this._tokenFromTagName(value.link.tagName);
                    v.linkTagName = value.link.tagName;
                    v.linkValueConverted = undefined;
                } else {
                    v.text = value.text ?? '';
                    v.linkTagName = undefined;
                    v.linkValueConverted = undefined;
                }
                return { ...item, value: v };
            })
        );
    }

    // * [Logo]

    /** URL para exibir o logo (resolve token → logo da empresa). */
    public logoUrl(item: DocumentGenericField): string {
        const v = item.value as GenericImageFieldPartial | undefined;
        const url = v?.url ?? '';
        if (!url) return '';
        if (v?.linkTagName || url.startsWith('$')) return this.companyLogo();
        return url;
    }

    public isUsingCompanyLogo(item: DocumentGenericField): boolean {
        const v = item.value as GenericImageFieldPartial | undefined;
        return !!v?.linkTagName || (v?.url ?? '').startsWith('$');
    }

    /** Usa a logo da empresa (token $COMPANY_LOGO). */
    public useCompanyLogo(item: DocumentGenericField): void {
        this._setLogo(item.fieldId, COMPANY_LOGO_TOKEN, COMPANY_LOGO_TAG);
    }

    /** Abre a galeria, faz upload e aplica a URL no logo. */
    public uploadLogo(item: DocumentGenericField): void {
        if (this.logoUploading()) return;
        this.logoUploading.set(true);
        this._media
            .selectImagesFromGallery(1)
            .pipe(
                switchMap(files => {
                    const file = files[0];
                    if (!file) return EMPTY;
                    const uploader = this.logoUploaderFn();
                    return uploader ? uploader(file) : this._editService.uploadHeaderLogo(file);
                })
            )
            .subscribe({
                next: url => {
                    this._setLogo(item.fieldId, url, undefined);
                    this.logoUploading.set(false);
                },
                error: () => this.logoUploading.set(false),
                complete: () => this.logoUploading.set(false),
            });
    }

    private _setLogo(fieldId: string | undefined, url: string, linkTagName?: string): void {
        if (!fieldId) return;
        this.items.update(list =>
            list.map(item => {
                if (item.fieldId !== fieldId) return item;
                const v: GenericImageFieldPartial = { ...(item.value as GenericImageFieldPartial) };
                v.url = url;
                v.linkTagName = linkTagName;
                v.linkValueConverted = undefined;
                return { ...item, value: v };
            })
        );
    }

    // * [Texto / comum]

    public iconFor(item: DocumentGenericField): string {
        return (item.fieldId && FIELD_ICONS[item.fieldId]) || 'global';
    }

    public titleFor(item: DocumentGenericField): string {
        return this._translate.instant(`document.edit.headerEditor.fields.${item.fieldId}.title`);
    }

    public descFor(item: DocumentGenericField): string {
        return this._translate.instant(
            `document.edit.headerEditor.fields.${item.fieldId}.description`
        );
    }

    public textValue(item: DocumentGenericField): string {
        return (item.value as GenericTextFieldPartial)?.text ?? '';
    }

    public linkTagNameOf(item: DocumentGenericField): string {
        return (item.value as GenericTextFieldPartial)?.linkTagName ?? '';
    }

    public limitCharOf(item: DocumentGenericField): number | undefined {
        return (item.value as GenericTextFieldPartial)?.limitChar;
    }

    public mainModal = viewChild<RaModalComponent>('mainModal');

    public animateClose(): void {
        this.mainModal()?.handleClose();
    }

    public confirmSave(): void {
        // Ver comentário em ModalFooterEditorComponent.confirmSave.
        this.save.emit({ header: this.items() });
    }

    private _tokenFromTagName(tagName: string): string {
        return '$' + tagName.replace(/^link_/, '').toUpperCase();
    }

    private _resolveDisplay(v: GenericTextFieldPartial, ctx: DataTagContext | null): string {
        if (v.linkValueConverted) return v.linkValueConverted;

        const isToken = !!v.linkTagName || (v.text ?? '').startsWith('$');
        if (isToken) {
            const tag = v.linkTagName || v.text || '';
            const resolved = resolveDataTagValue(tag, ctx);
            if (resolved != null && String(resolved).trim() !== '') return String(resolved);
            return getDataTagLabel(this._translate, tag);
        }
        return v.text ?? '';
    }
}
